import React, { useState, useRef, useEffect } from "react";
import {
  KnowledgePageContainer,
  KnowledgePageHeaderWrapper,
} from "./KnowledgeViewPage.styled";
import { DataAccesBar } from "../../components/DataAccesbar";
import { DataAccessHeader } from "../../components/DataAccessHeader";
import { SlideoutDrives } from "../../components/SlideoutDrives";
import { Outlet } from "react-router-dom";
import SlideoutDriveDetail from "../../components/SlideoutDriveDetail/SlideoutDriveDetail";
import SlideoutDocumentDetail from "../../components/SlideoutDocumentDetail/SlideoutDocumentDetail";
import { v4 as uuidv4 } from "uuid";
import { useStore } from "../../../../../../../hooks";
import Alerts from "../../../../../../../elements/v2/Alerts/Alerts";
import { useKnowledgeInitialization } from "../../hooks/useKnowledgeInitialization";

const KnowledgePageView = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [showSlideout, setShowSlideout] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [showSlideoutDriveDetail, setShowSlideoutDriveDetail] = useState(false);
  const [showSlideoutDocumentDetail, setShowSlideoutDocumentDetail] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { fileStore, authStore, knowledgeStore } = useStore();
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const {
    error: initError,
    retryInitialization,
    resetInitialization,
  } = useKnowledgeInitialization();

  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  const handleBrowseDriveClick = () => {
    setShowSlideout(true);
    setShowMenu(false);
  };

  const handleCloseSlideout = () => {
    setShowSlideout(false);
  };

  const handleCloseSlideoutDriveDetail = () => {
    setShowSlideoutDriveDetail(false);
  };

  const handleCloseSlideoutDocumentDetail = () => {
    setShowSlideoutDocumentDetail(false);
    setSelectedDocument(null);
  };

  const handleUploadToAgent = () => {
    fileInputRef.current?.click();
  };

  const handleCloseMenu = () => {
    setShowMenu(false);
  };

  const handleCloseAlert = () => {
    setAlert(null);
  };

 /* const handleTabChange = () => {
    resetInitialization();
  }; */

  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setAlert({
        title: "Error",
        message: "The file is too large. Maximum size: 100MB",
        severity: "error",
      });
      event.target.value = "";
      return;
    }

    if (!authStore.selectedAccount?.id || (!authStore.apiUserId && !authStore.userId)) {
      setAlert({
        title: "Error",
        message: "Authentication data is incomplete. Please log in again.",
        severity: "error",
      });
      event.target.value = "";
      return;
    }

    // Get user ID from token (apiUserId) or fallback to Cognito userId
    const userId = authStore.apiUserId || authStore.userId;

    try {
      const createFileBody = {
        name: file.name,
        folder_id: uuidv4(),
        tenant_id: "58145bef-4f71-468a-bded-1823d2a08f58",
        user_id: userId,
        file_type: file.type || "application/octet-stream",
        status: "active",
      };

      const createFileResponse = await fileStore.createFile(createFileBody);

      if (createFileResponse?.uploadUrl) {
        const uploadUrl = createFileResponse.uploadUrl;

        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
        });

        if (uploadResponse.ok) {
          const createDocumentBody = {
            name: file.name,
            vault_id: 1,
            created_by: userId,
            owner: userId,
            tenant_id: "58145bef-4f71-468a-bded-1823d2a08f58",
            type: file.type || "application/octet-stream",
            url: uploadUrl,
            metadata: {
              source: "file_upload",
              version: "1.0",
            },
          };
          

          const createDocumentResponse = await fileStore.createDocument(
            createDocumentBody
          );

          const attachDocumentBody = {
            s3_key: `documents/${file.name}`,
            document_uuid: createDocumentResponse.uuid,
            tenant_id: "58145bef-4f71-468a-bded-1823d2a08f58",
          };

          await fileStore.attachDocument(attachDocumentBody);
          event.target.value = "";

          setAlert({
            title: "Success",
            message: "File uploaded successfully",
            severity: "success",
          });
        } else {
          throw new Error(`Error uploading file: ${uploadResponse.status}`);
        }
      } else {
        throw new Error("Error: No received upload URL");
      }
    } catch (error) {
      let errorMessage = "Error uploading file";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlert({
        title: "Error",
        message: errorMessage,
        severity: "error",
      });
      event.target.value = "";
    }
  };

  if (initError) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          flexDirection: "column",
          padding: "20px",
        }}
      >
        <Alerts
          title="Initialization Error"
          message={initError}
          severity="error"
          onClose={() => {}}
          top="20px"
          right="20px"
          width="400px"
        />
        <button
          onClick={retryInitialization}
          style={{
            marginTop: "16px",
            padding: "10px 20px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry Initialization
        </button>
      </div>
    );
  }

  return (
    <KnowledgePageContainer>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileSelect}
        accept="*/*"
      />

      {alert && (
        <Alerts
          title={alert.title}
          message={alert.message}
          severity={alert.severity}
          onClose={handleCloseAlert}
          top="20px"
          right="20px"
          width="400px"
        />
      )}

      <KnowledgePageHeaderWrapper>
        <DataAccessHeader
          onClickAdd={handleMenuClick}
          menuRef={menuRef}
          showMenu={showMenu}
          onUploadToAgent={handleUploadToAgent}
          onBrowseDrives={handleBrowseDriveClick}
          onCloseMenu={handleCloseMenu}
        />
        <DataAccesBar
          viewMode={viewMode}
          setViewMode={setViewMode}
          //onTabChange={handleTabChange}
          onSearchChange={handleSearchChange}
        />
      </KnowledgePageHeaderWrapper>
      <Outlet context={{ viewMode, setShowSlideoutDriveDetail, setShowSlideoutDocumentDetail, setSelectedDocument, searchTerm }} />
      <SlideoutDrives open={showSlideout} onClose={handleCloseSlideout} />
      <SlideoutDriveDetail
        open={showSlideoutDriveDetail}
        onClose={handleCloseSlideoutDriveDetail}
      />
      <SlideoutDocumentDetail
        open={showSlideoutDocumentDetail}
        onClose={handleCloseSlideoutDocumentDetail}
        documentData={selectedDocument}
      />
    </KnowledgePageContainer>
  );
};

export default KnowledgePageView;